import { Observable } from 'rxjs/Observable';
import { AccountApi } from '../parsers';
import { fetchAccount } from '../utils/api/index';
import { alias, identify } from '../utils/fastpanel';
import get from 'lodash/get';
import { combineEpics } from 'redux-observable';
import mergeMap from 'rxjs/add/operator/mergeMap';

import {
  ACCOUNT_FETCH_FAILED,
  ACCOUNT_FETCH_REQUESTED,
  ACCOUNT_FETCH_SUCCEEDED,
  LOG_IN_SUCCEEDED,
  SIGN_UP_SUCCEEDED,
  accountFetchFailed,
  accountFetchSucceeded,
  facebookLoginSucceded,
  facebookSignUpSucceded,
  googleLoginSucceeded,
  googleSignUpSucceded,
  loginSucceeded,
  requestAccount,
  signUpSucceeded,
} from '../redux/account';

import { clearLastLinkClicked } from '../redux/tracking';

import type { ActionObs, Store, Obs } from '../types';

export function accountRequestedEpic(action$: ActionObs, store: Store): Obs {
  return action$.ofType(ACCOUNT_FETCH_REQUESTED).switchMap(() =>
    fetchAccount()
      .map(resp => AccountApi.parse(resp))
      .map(({ account }) => accountFetchSucceeded(account))
      .catch(err => Observable.of(accountFetchFailed())),
  );
}

export function socialAccountConnectedEpic(
  action$: ActionObs,
  store: Store,
): Obs {
  return action$.ofType(ACCOUNT_FETCH_SUCCEEDED).mergeMap(action => {
    const { account } = action.payload;
    const state = store.getState();

    const { id: identifier } = get(state, 'tracking.lastLink', {});
    const socialSignup = get(state, 'account.socialSignup', false);

    if (socialSignup) {
      return Observable.empty();
    }

    switch (identifier) {
      case 'google-login':
        return Observable.of(
          googleLoginSucceeded(account),
          loginSucceeded(account, 'google'),
        );

      //eslint-disable-next-line no-fallthrough
      case 'facebook-login':
        return Observable.of(
          facebookLoginSucceded(account),
          loginSucceeded(account, 'facebook'),
        );

      case 'google-signup':
        return Observable.of(
          googleSignUpSucceded(account),
          signUpSucceeded(account, 'google'),
        );

      //eslint-disable-next-line no-fallthrough
      case 'facebook-signup':
        return Observable.of(
          facebookSignUpSucceded(account),
          signUpSucceeded(account, 'facebook'),
        );

      default:
        // re-emit the result if this wasn't a google or facebook signup
        return Observable.empty();
    }
  });
}

export function accountConnectedEpic(action$: ActionObs, store: Store): Obs {
  return action$
    .ofType(ACCOUNT_FETCH_SUCCEEDED, LOG_IN_SUCCEEDED, SIGN_UP_SUCCEEDED)
    .mergeMap(
      ({
        type,
        payload: {
          account: { id },
        },
      }) => {
        if (type === SIGN_UP_SUCCEEDED) {
          alias(id);
        }
        identify(id);
        return Observable.empty();
      },
    );
}

export function accountLoggedInEpic(action$: ActionObs, store: Store): Obs {
  return action$
    .ofType(LOG_IN_SUCCEEDED, SIGN_UP_SUCCEEDED)
    .switchMap(() =>
      Observable.concat(
        Observable.of(clearLastLinkClicked()),
        Observable.of(requestAccount()),
      ),
    );
}

export function redirectAfterAuthentication(
  action$: ActionObs,
  store: Store,
): Obs {
  return action$.ofType(LOG_IN_SUCCEEDED, SIGN_UP_SUCCEEDED).switchMap(() => {
    const redirectUrl = get(store.getState(), 'modals.redirectUrl', null);

    // If there is a redirect url, use it.
    if (redirectUrl) {
      // No Router.push() because it's always outside NextJS...
      window.location.href = redirectUrl;
    }

    return Observable.empty();
  });
}

export function redirectUnauthenticatedUsers(
  action$: ActionObs,
  store: Store,
): Obs {
  return action$
    .ofType(ACCOUNT_FETCH_FAILED)
    .filter(() => get(store.getState(), 'router.result.requiresAuth', false))
    .switchMap(() => Observable.of(replace('/')));
}

/**
 * We need to do this because the sign up and log in API is not guaranteed to return all the
 * information as the account fetch API.
 *
 * In the past, we needed fewer account properties, but as we are expanding we need to have a
 * robust data model of the account in order for our application to function.
 */
export function fetchAccountOnAuth(action$: ActionObs, store: Store): Obs {
  return action$.ofType(SIGN_UP_SUCCEEDED, LOG_IN_SUCCEEDED).switchMap(() => {
    return Observable.concat(
      Observable.of(clearLastLinkClicked()),
      Observable.of(requestAccount()),
    );
  });
}

export default combineEpics(
  accountConnectedEpic,
  accountRequestedEpic,
  accountLoggedInEpic,
  fetchAccountOnAuth,
  redirectAfterAuthentication,
  redirectUnauthenticatedUsers,
  socialAccountConnectedEpic,
);
